import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Filter, Download, Eye, Edit2, Trash2, Plus, Loader2, Share2, MessageCircle, Clock, CheckCircle2, AlertTriangle, Copy, Check } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';

import Pagination from '../../components/ui/pagination';

const VendorList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('is_active', statusFilter === 'active' ? 'true' : 'false');
      if (searchQuery) params.append('search', searchQuery);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const res = await fetch(`${backendUrl}/api/vendors?${params}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch vendors');
      const data = await res.json();
      setVendors(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch vendors", variant: "destructive" });
    } finally { setLoading(false); }
  }, [backendUrl, getAuthHeaders, statusFilter, searchQuery, page, pageSize, toast]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);
  useEffect(() => { setPage(1); }, [statusFilter]);
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); fetchVendors(); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line

  const handleDelete = async () => {
    if (!vendorToDelete) return;
    try {
      const res = await fetch(`${backendUrl}/api/vendors/${vendorToDelete.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to delete vendor');
      toast({ title: "Success", description: "Vendor deleted successfully" });
      fetchVendors();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete vendor", variant: "destructive" });
    } finally { setDeleteDialogOpen(false); setVendorToDelete(null); }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Vendors List</h1>
          <p className="text-text-secondary font-ibm mt-1">Manage your vendor database</p>
        </div>
        <Button
          data-testid="add-vendor-btn"
          onClick={() => navigate('/vendors/add')}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg"
        >
          <Plus className="w-5 h-5 mr-2" />New Vendor
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-primary-blue text-white rounded-xl shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold font-manrope">All Vendors ({vendors.length})</h3>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                <Input
                  data-testid="vendor-search-input"
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 font-ibm"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="vendor-status-filter" className="w-40 bg-white/10 border-white/20 text-white focus:bg-white/20 font-ibm">
                  <Filter className="w-4 h-4 mr-2" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid="export-vendors-btn" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white font-ibm font-semibold">
                <Download className="w-4 h-4 mr-2" />Export
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
            <span className="ml-2 text-text-secondary font-ibm">Loading vendors...</span>
          </div>
        ) : vendors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-text-secondary font-ibm mb-4">No vendors found</p>
            <Button onClick={() => navigate('/vendors/add')} className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm">
              <Plus className="w-4 h-4 mr-2" />Add First Vendor
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="vendors-table">
              <thead className="bg-gray-50/50 border-b border-gray-200">
                <tr>
                  {['Business Name & PAN','Contact & Location','GST Number','Branches','Status','KYC Status','Actions'].map(h => (
                    <th key={h} className={`px-6 py-4 text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm ${['Branches','Status','KYC Status','Actions'].includes(h) ? 'text-center' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vendors.map(vendor => (
                  <tr key={vendor.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-text-dark font-ibm block">{vendor.business_name}</span>
                      {vendor.pan_number ? (
                        <span className="inline-block text-[11px] text-text-secondary font-mono mt-0.5 bg-gray-100 px-1.5 py-0.5 rounded">
                          PAN: {vendor.pan_number}
                        </span>
                      ) : (
                        <span className="text-[11px] text-amber-600 font-ibm">PAN Missing</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-text-dark font-ibm">{vendor.contact_person || '—'}</div>
                      <div className="text-xs text-text-secondary font-ibm mt-0.5">
                        {vendor.contact_phone || vendor.contact_email || vendor.head_office_location || '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-text-secondary font-mono font-medium">{vendor.head_office_gst || '—'}</span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="inline-flex items-center justify-center w-8 h-8 bg-accent-blue/10 rounded-full">
                        <span className="text-xs font-bold text-accent-blue font-ibm">{vendor.branch_count}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Badge className={`font-ibm font-semibold border-0 text-[11px] ${vendor.is_active ? 'bg-accent-green/15 text-accent-green' : 'bg-gray-200 text-gray-600'}`}>
                        {vendor.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {vendor.kyc_status === 'VERIFIED' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Verified
                        </span>
                      ) : vendor.kyc_status === 'SUBMITTED' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          <Clock className="w-3.5 h-3.5 text-blue-600" /> Submitted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-1.5">
                        {/* Quick WhatsApp Share */}
                        <button
                          onClick={() => {
                            const link = `${window.location.origin}/vendor-kyc-onboarding/${vendor.kyc_token}`;
                            const msg = encodeURIComponent(`Dear ${vendor.business_name},\n\nPlease complete your Vendor Empanelment & KYC submission at:\n${link}\n\nThank you.`);
                            window.open(`https://wa.me/?text=${msg}`, '_blank');
                          }}
                          className="w-8 h-8 flex items-center justify-center hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                          title="Share KYC Link via WhatsApp"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </button>

                        {/* Quick Copy Link */}
                        <button
                          onClick={() => {
                            const link = `${window.location.origin}/vendor-kyc-onboarding/${vendor.kyc_token}`;
                            navigator.clipboard.writeText(link);
                            toast({ title: "KYC Link Copied", description: "Vendor onboarding link copied to clipboard!" });
                          }}
                          className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                          title="Copy Vendor KYC Link"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>

                        <button data-testid={`view-vendor-${vendor.id}`} onClick={() => navigate(`/vendors/view/${vendor.id}`)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors" title="View"><Eye className="w-4 h-4 text-text-secondary" /></button>
                        <button data-testid={`edit-vendor-${vendor.id}`} onClick={() => navigate(`/vendors/edit/${vendor.id}`)} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors" title="Edit"><Edit2 className="w-4 h-4 text-text-secondary" /></button>
                        <button data-testid={`delete-vendor-${vendor.id}`} onClick={() => { setVendorToDelete(vendor); setDeleteDialogOpen(true); }} className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg transition-colors" title="Delete"><Trash2 className="w-4 h-4 text-accent-red" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && vendors.length > 0 && (
          <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        )}
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{vendorToDelete?.business_name}"? This will also delete all associated branches and KYC documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-accent-red hover:bg-accent-red/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorList;
